/**
 * Pricing is stored and calculated in kilometres by the backend, regardless of the operator's country.
 * The country's `distance_unit` only decides how those values are presented, so every distance and
 * per-distance rate is converted on the way in and back to kilometres on the way out.
 *
 * The same applies to distances we only ever display — a booking's actual and estimated distance, the
 * journey estimate on the map — which arrive metric and are converted here for presentation only.
 */
const KM_PER_MILE = 1.609344;
const FEET_PER_METRE = 3.280839895;

const round = (value: number, precision: number) => Number(Number(value).toFixed(precision));

export const isImperialUnit = (unit?: string | null) => (unit ?? "").trim().toLowerCase() === "mi";

/** Noun for use in copy, e.g. "Set up kilometre-based pricing". */
export const distanceUnitNoun = (unit?: string | null) => (isImperialUnit(unit) ? "mile" : "kilometre");

/** Distance stored in km, displayed in the operator's unit. */
export const kmToUnit = (km: number, unit?: string | null, precision = 2) =>
  isImperialUnit(unit) ? round(Number(km) / KM_PER_MILE, precision) : Number(km);

/** Distance entered in the operator's unit, stored in km. */
export const unitToKm = (value: number, unit?: string | null, precision = 3) =>
  isImperialUnit(unit) ? round(Number(value) * KM_PER_MILE, precision) : Number(value);

/** Rate stored per km, displayed per the operator's unit. */
export const ratePerKmToUnit = (ratePerKm: number, unit?: string | null, precision = 2) =>
  isImperialUnit(unit) ? round(Number(ratePerKm) * KM_PER_MILE, precision) : Number(ratePerKm);

/** Rate entered per the operator's unit, stored per km. */
export const ratePerUnitToKm = (ratePerUnit: number, unit?: string | null, precision = 4) =>
  isImperialUnit(unit) ? round(Number(ratePerUnit) / KM_PER_MILE, precision) : Number(ratePerUnit);

/**
 * Rounding a km value into miles and straight back does not land on the original number
 * (10km displays as 6.21mi, which converts back to 9.99km). When the operator left a field
 * untouched we send the stored km value back unchanged instead of the round-tripped one.
 */
export const unitToKmPreserving = (value: number, originalKm: number | null | undefined, unit?: string | null) => {
  if (!isImperialUnit(unit)) return Number(value);
  if (originalKm != null && Number(value) === kmToUnit(originalKm, unit)) return Number(originalKm);

  return unitToKm(value, unit);
};

/** As per unitToKmPreserving, for currency-per-distance rates. */
export const ratePerUnitToKmPreserving = (value: number, originalRatePerKm: number | null | undefined, unit?: string | null) => {
  if (!isImperialUnit(unit)) return Number(value);
  if (originalRatePerKm != null && Number(value) === ratePerKmToUnit(originalRatePerKm, unit)) return Number(originalRatePerKm);

  return ratePerUnitToKm(value, unit);
};

/**
 * A short distance — the metres between two tracked points, not a whole trip.
 *
 * Miles are the wrong scale here: a 120m leg is 0.07mi, which tells the reader nothing. An imperial
 * operator gets feet instead, which is the unit they would actually use for this.
 */
export const formatShortDistance = (metres: number, unit?: string | null): string =>
  isImperialUnit(unit) ? `${Math.round(Number(metres) * FEET_PER_METRE)} ft` : `${Math.round(Number(metres))} m`;
